import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import musicIcon from "@assets/images/musicIcon.webp";
import musicBorder from "@assets/images/borders/music-border.webp";
import PlayIconAudio from "@assets/images/PlayIconAudio.svg";
import PauseIconAudio from "@assets/images/pause-icon.svg";
import { currencyFormatter } from "@src/utils/CardValidationHelper";
import { UserContext } from "@src/context/UserContext";
import { LoginContext } from "@src/context/LoginContext";
import { getValueOrDefault } from "@src/utils/ConditionHelper";
import {
  convertSecondsIntoHours,
  convertSecondsIntoHoursWithoutZero,
} from "@src/utils/TimerHelper";
import { convertToCommaSeparatedValue } from "@src/utils/CommaSeparateValueHelper";
import MusicPlayingGif from "@assets/Gifs/Music-playing.gif";
import { Tooltip } from "react-tooltip";
import { getImageUrlWithExpiry } from "@src/utils/ImageExpirtyHelper";
import useIsMobile from "@src/hooks/useIsMobile";
import { useSearchParams } from "react-router-dom";
import "@src/styles/AudioItemStyle.scss";
import type { IAudioItem } from "@src/types/AppTypes";

const AudioItem: React.FC<IAudioItem> = ({
  audio,
  handleMusicItemPreviewClick,
  addToWishlist,
  index,
  handleMusicAddToCart,
  selectedAudio,
  setSelectedAudio,
  isPlaying,
  isMusicAddingLoading,
  setIsSeletedType,
  handleAudioClick,
}) => {
  const user = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { setLogin } = useContext(LoginContext);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
  const [previewInPlayMode, setPreviewInPlayMode] =
    useState<boolean>(isPlaying); // for manage mode - isPlaying or not
  const audioPreviewRef = useRef<HTMLAudioElement | null>(null);
  const audioref = useRef<HTMLDivElement | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState<boolean>(false); // for manage play and pause button
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(true); // for manage loader if audio is loading or fetching further data to play
  const [audioTimer, setAudioTimer] = useState<string>("00:00");

  const [progress, setProgress] = useState(0);
  const size = 180; // Diameter of the SVG
  const strokeWidth = 8; // Thickness of the stroke
  const center = size / 2; // Center of the circle
  const radius = center - strokeWidth / 2; // Radius of the circle
  const circumference = 2 * Math.PI * radius; // Circumference of the circle
  const offset = circumference - (progress / 100) * circumference;
  const rafIdRef = useRef<number | null>(null);
  const isMobile = useIsMobile();

  // for handle play and pause button
  const handlePreviewPlayAndPause = useCallback(() => {
    setIsSeletedType && setIsSeletedType("music");
    if (audioPreviewRef.current) {
      if (isPreviewPlaying) {
        audioPreviewRef.current.pause();
        setIsSeletedType && setIsSeletedType(undefined);
        setIsPreviewPlaying(false);
        return false;
      } else {
        audioPreviewRef.current.play();
        setIsSeletedType && setIsSeletedType("music");
        setIsPreviewPlaying(true);
        return false;
      }
    } else {
      return true;
    }
  }, [isPreviewPlaying, setIsSeletedType]);

  // Function to start updating the timer using requestAnimationFrame
  const updateAudioProgress = useCallback(() => {
    const audio = audioPreviewRef.current;
    if (!audio) return;

    const update = () => {
      const { timeWithoutHours } = convertSecondsIntoHours(
        audio.currentTime,
        audio.duration
      );
      setProgress(
        Number(((audio.currentTime / audio.duration) * 100).toFixed(0))
      );
      setAudioTimer(timeWithoutHours);

      if (!audio.paused && !audio.ended) {
        rafIdRef.current = requestAnimationFrame(update);
      }
    };

    update();
  }, [audioPreviewRef]);

  // Clean up function to stop the animation frame when audio is paused or component is unmounted
  const stopUpdatingProgress = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
  };

  useEffect(() => {
    const audio = audioPreviewRef.current;

    if (audio) {
      audio.addEventListener("play", updateAudioProgress);
      audio.addEventListener("pause", stopUpdatingProgress);
      audio.addEventListener("ended", stopUpdatingProgress);

      return () => {
        audio.removeEventListener("play", updateAudioProgress);
        audio.removeEventListener("pause", stopUpdatingProgress);
        audio.removeEventListener("ended", stopUpdatingProgress);
        stopUpdatingProgress();
      };
    }
  }, [audioPreviewRef, updateAudioProgress, previewInPlayMode, isPlaying]);

  useEffect(() => {
    if (!isPlaying) {
      audioPreviewRef.current?.pause();
      setIsPreviewLoading(true);
      setIsPreviewPlaying(false);
      setProgress(0);
      setAudioTimer("00:00");
    }
  }, [isPlaying]);

  useEffect(() => {
    if (audioref.current) {
      const observer = new IntersectionObserver(
        ([entries]) => {
          if (!entries.isIntersecting && audioPreviewRef.current) {
            audioPreviewRef?.current!.pause();
            setProgress(0);
            setAudioTimer("00:00");
            setPreviewInPlayMode(false);
            setIsPreviewPlaying(false);
            setIsPreviewLoading(false);
            setSelectedAudio(undefined);
          }
        },
        { root: null, rootMargin: "0px", threshold: 0 }
      );

      const currentaudio = audioref.current;

      if (currentaudio) {
        observer.observe(currentaudio);
      }

      return () => {
        observer.unobserve(currentaudio);
        observer.disconnect();
      };
    }
  }, [isPreviewPlaying, setIsSeletedType, setSelectedAudio]);

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  useEffect(() => {
    if (sessionStorage.getItem("SM") === audio.id.toString()) {
      document
        .getElementById(`audio-item-${Number(sessionStorage.getItem("SM"))}`)
        ?.scrollIntoView({ block: "center" });
      sessionStorage.removeItem("SM");
      setIsHovered(true);
    }
  }, [audio.id]);

  return (
    <div
      className={`relative ms-5 max-md:ms-0 ${isHovered ? "detail-popup" : ""}`}
    >
      <div
        ref={audioref}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (audio.is_purchased) handleMusicItemPreviewClick(audio);
          else return;
        }}
        role="button"
        className="duration-300 max-[512px]:w-full max-[512px]:m-0 detail-mini-box"
        id={`audio-item-${audio.id}`}
      >
        <div className="relative h-72 mb-3 overflow-hidden group rounded-[10px_35px_25px_40px]">
          {audio.is_purchased && (
            <div className="-rotate-45 absolute top-8 -left-14 z-[12] border-2 border-[#D854BB] py-2 bg-[url('@assets/images/BokehBlueBG.webp')] bg-cover w-[220px] text-center">
              <p className="text-2xl text-[#FDDA35]">PURCHASED</p>
            </div>
          )}
          <img
            className="w-full h-full absolute top-0 left-0 z-10"
            src={musicBorder}
            alt=""
          />

          {(audio.trailer || audio.is_purchased) && (
            <>
              <div className="text-center w-full h-full absolute top-2/4 left-2/4 translate-x-[-50%] translate-y-[-50%] z-10 object-contain flex flex-col items-center justify-center gap-3 max-md:gap-2 rounded-[10px_35px_25px_40px]">
                {!audio.is_purchased && (
                  <svg
                    width={size}
                    height={size}
                    style={{ transform: "rotate(-90deg)" }}
                  >
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      strokeWidth={strokeWidth}
                      fill="none"
                      stroke="#931878"
                    />
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      strokeWidth={strokeWidth}
                      fill="none"
                      stroke="#E6E5E2"
                      strokeDasharray={circumference}
                      strokeDashoffset={offset}
                      style={{
                        transition: isPlaying
                          ? "stroke-dashoffset 0.5s ease"
                          : "",
                        strokeLinecap: "round",
                      }}
                    />
                  </svg>
                )}

                <div className="absolute flex items-center justify-center flex-col">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const isFirstTimeClick = handlePreviewPlayAndPause();
                      if (isFirstTimeClick) {
                        if (audio.is_purchased) {
                          handleMusicItemPreviewClick(audio);
                        } else {
                          setProgress(0);
                          setIsPreviewLoading(true);
                          setSelectedAudio(audio);
                          setPreviewInPlayMode(true);
                        }
                      }
                    }}
                    className="h-16 w-16 bg-[#1D1159E5] rounded-full p-3 border-4 border-secondary"
                  >
                    {isPlaying && isPreviewPlaying ? (
                      <img
                        src={PauseIconAudio}
                        alt=""
                        className="w-full h-full"
                      />
                    ) : (
                      <img
                        src={PlayIconAudio}
                        alt=""
                        className="w-full h-full"
                      />
                    )}
                  </button>
                  {audio.is_purchased ? (
                    <span className="text-secondary text-center text-lg text-border-1">
                      Play Media
                    </span>
                  ) : isPlaying ? (
                    <span className="text-secondary text-center text-lg text-border-1">
                      {audioTimer}
                    </span>
                  ) : (
                    <span className="text-secondary text-center text-lg text-border-1">
                      Preview media
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
          {isImageLoading && (
            <div className="skeleton w-full h-full absolute !rounded-[10px_35px_25px_40px] z-[0]"></div>
          )}
          {isPlaying && previewInPlayMode && (
            <div className="w-full h-full absolute">
              {isPreviewLoading && (
                <div className="w-full h-full absolute z-50">
                  <p className="video-loader left-[45%] -translate-x-1/2 absolute top-[105px]"></p>
                </div>
              )}
              {isPreviewPlaying && (
                <div className="absolute bottom-3 right-6 z-10">
                  <img
                    src={MusicPlayingGif}
                    className="w-9 h-7"
                    alt="music-playing-gif"
                  />
                </div>
              )}
              <audio
                ref={audioPreviewRef}
                src={selectedAudio?.trailer}
                controls
                className="absolute hidden"
                onEnded={() => {
                  setProgress(0);
                  setAudioTimer("00:00");
                  setPreviewInPlayMode(false);
                  audioPreviewRef.current = null;
                  setIsPreviewPlaying(false);
                  setIsPreviewLoading(false);
                  setSelectedAudio(undefined);
                }}
                onPlay={() => {
                  setIsPreviewPlaying(true);
                }}
                onError={() => {
                  setIsPreviewLoading(false);
                }}
                onPause={() => {
                  setIsPreviewPlaying(false);
                }}
                onWaiting={() => {
                  setIsPreviewLoading(true);
                }}
                onCanPlay={() => {
                  setIsPreviewLoading(false);
                }}
                onLoadedMetadata={() => {
                  if (audioPreviewRef.current) audioPreviewRef.current.play();
                  setIsPreviewPlaying(true);
                  setIsPreviewLoading(false);
                }}
              />
            </div>
          )}
          <div className="absolute bottom-3 right-6">
            <img
              src={MusicPlayingGif}
              className="w-9 h-7"
              alt="music-playing-gif"
            />
          </div>
          <img
            className="w-full object-cover blur-sm bg-gradient-to-t from-[#184AF4] to-[#182D73] h-full absolute top-0 left-0"
            src={getValueOrDefault(
              getImageUrlWithExpiry(audio?.thumbnail),
              musicIcon
            )}
            alt="music-preview-image"
          />
          <img
            className="w-full object-contain h-full relative"
            src={getValueOrDefault(
              getImageUrlWithExpiry(audio?.thumbnail),
              musicIcon
            )}
            onLoad={() => {
              setIsImageLoading(false);
            }}
            loading="lazy"
            onError={() => {
              setTimeout(() => {
                setIsImageLoading(false);
              }, 3000);
            }}
            alt="music-preview-image"
          />
        </div>
        <div
          onMouseEnter={() => {
            if (window.location.pathname.includes("allmusic") && !isMobile) {
              handleMouseEnter();
            }
          }}
          onMouseLeave={() => {
            if (window.location.pathname.includes("allmusic") && !isMobile) {
              handleMouseLeave();
            }
          }}
          className={`relative z-10 -top-6 -left-5 h-[21rem] max-sm:h-fit max-sm:min-h-64 max-md:left-0 bg-[#1d1159bf] rounded-[10px_35px_25px_40px] ${
            audio.is_purchased || !user ? "cursor-pointer" : "cursor-auto"
          }`}
          onClick={() => {
            if (isMobile) {
              handleAudioClick(audio, index || 0);
            } else {
              if (audio.is_purchased) {
                handleMusicItemPreviewClick(audio);
              } else {
                if (!user) {
                  setLogin({ type: "updateLogin", payload: true });
                  if (isMobile) {
                    searchParams.set("tab", "login");
                    setSearchParams(searchParams, { replace: true });
                  }
                  return;
                }
              }
            }
          }}
        >
          <div className="p-8 max-2xl:px-6 max-sm:pe-4 max-sm:ps-6 break-words h-full flex flex-col items-start justify-between gap-4">
            <div className="w-full">
              <div className="flex max-sm:flex-wrap items-center justify-between gap-3 mb-1 w-full">
                <p className="flex items-center text-lg max-sm:text-base gap-2 text-primary font-mikado-medium">
                  <i className="ri-music-2-line max-md:text-xl"></i> Music
                </p>

                <div className="flex items-center gap-2">
                  {index !== undefined && addToWishlist && (
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (
                          addToWishlist &&
                          index !== undefined &&
                          !audio.is_purchased
                        )
                          await addToWishlist(audio.id, index);
                      }}
                      className={`flex justify-center w-[36px] ${
                        audio.is_purchased
                          ? "opacity-50 cursor-auto"
                          : "opacity-100"
                      }`}
                      aria-label="Like"
                      data-tooltip-id="audio-wishlist-tooltip"
                      data-tooltip-content={
                        !audio.is_purchased
                          ? ""
                          : "You cannot add this music to your wishlist because you have already purchased it."
                      }
                    >
                      {audio.is_wishlist ? (
                        <i className="ri-heart-fill text-4xl max-md:text-3xl text-primary-gradient"></i>
                      ) : (
                        <i className="ri-heart-line text-4xl max-md:text-3xl text-primary-gradient"></i>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-primary text-2xl max-sm:text-xl font-mikado-bold line-clamp-1 w-full break-all mb-3">
                {audio.title}
              </p>
              <p className="text-[#9CA3AF] text-xl max-sm:text-lg font-mikado-regular line-clamp-1 w-full break-all media-description">
                Performed by {convertToCommaSeparatedValue(audio.perform_by)}
              </p>
              <p className="text-[#9CA3AF] text-xl max-sm:text-lg font-mikado-regular line-clamp-1 w-full break-all media-description">
                Produced by {convertToCommaSeparatedValue(audio?.producers)}
              </p>
              <p className="text-[#9CA3AF] text-xl max-sm:text-lg font-mikado-regular line-clamp-1 w-full break-all media-description">
                {
                  convertSecondsIntoHoursWithoutZero(audio.total_duration, 0)
                    .timeInWords
                }
              </p>
            </div>
            <div className="flex items-center justify-between w-full gap-4">
              <>
                {!audio.is_purchased && (
                  <button
                    className="flex 2xl:min-w-[159px] max-md:min-w-[145px] justify-center text-[#1D1159] bg-[#d854ba] bg-[url('@assets/ui-helpers/button/ButtonDarkBorder.png')] rounded-3xl bg-no-repeat bg-cover bg-center py-4 px-4 2xl:px-9 duration-150 hover:scale-95 disabled:hover:scale-100 disabled:opacity-80 disabled:cursor-not-allowed disabled:duration-0 flex-shrink-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!user) {
                        setLogin({ type: "updateLogin", payload: true });
                        if (isMobile) {
                          searchParams.set("tab", "login");
                          setSearchParams(searchParams, { replace: true });
                        }
                        localStorage.setItem("lID", audio.id.toString());
                        localStorage.setItem("letype", "Music");
                        return;
                      }
                      handleMusicAddToCart(audio.id, index || 0);
                    }}
                    disabled={audio.is_added_purchased || isMusicAddingLoading}
                    aria-label="add-cart-button"
                  >
                    {audio.is_added_purchased ? "Added" : "Add to cart"}
                  </button>
                )}
                {audio.is_purchased && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMusicItemPreviewClick(audio);
                    }}
                    className="flex min-w-[159px] justify-center text-[#1D1159] bg-[#d854ba] bg-[url('@assets/ui-helpers/button/ButtonDarkBorder.png')] rounded-3xl bg-no-repeat bg-cover bg-center py-4 px-9 duration-150 hover:scale-95"
                    aria-label="listen-button"
                  >
                    LISTEN
                  </button>
                )}
              </>
              <p className="text-primary font-mikado-medium">
                {audio.is_purchased
                  ? ""
                  : "Listen Now " + currencyFormatter(Number(audio.price))}
              </p>
            </div>
          </div>
          <img
            className="w-full h-full absolute top-0 left-0 -z-[1]"
            src={musicBorder}
            alt="music-border"
          />
        </div>
      </div>

      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (audio.is_purchased) handleMusicItemPreviewClick(audio);
          else return;
        }}
        role="button"
        className="duration-300 mx-[22px] max-[512px]:w-full max-[512px]:m-0 detail-large-box"
        id={`audio-item-${audio.id}`}
      >
        <div className="relative h-96 mb-3 overflow-hidden group rounded-[10px_35px_25px_40px]">
          {audio.is_purchased && (
            <div className="-rotate-45 absolute top-8 -left-14 z-[12] border-2 border-[#D854BB] py-2 bg-[url('@assets/images/BokehBlueBG.webp')] bg-cover w-[220px] text-center">
              <p className="text-2xl text-[#FDDA35]">PURCHASED</p>
            </div>
          )}
          <img
            className="w-full h-full absolute top-0 left-0 z-10"
            src={musicBorder}
            alt=""
          />

          {(audio.trailer || audio.is_purchased) && (
            <>
              <div className="text-center w-full h-full absolute top-2/4 left-2/4 translate-x-[-50%] translate-y-[-50%] z-10 object-contain flex flex-col items-center justify-center gap-3 max-md:gap-2 rounded-[10px_35px_25px_40px]">
                {!audio.is_purchased && (
                  <svg
                    width={size}
                    height={size}
                    style={{ transform: "rotate(-90deg)" }}
                  >
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      strokeWidth={strokeWidth}
                      fill="none"
                      stroke="#931878"
                    />
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      strokeWidth={strokeWidth}
                      fill="none"
                      stroke="#E6E5E2"
                      strokeDasharray={circumference}
                      strokeDashoffset={offset}
                      style={{
                        transition: isPlaying
                          ? "stroke-dashoffset 0.5s ease"
                          : "",
                        strokeLinecap: "round",
                      }}
                    />
                  </svg>
                )}

                <div className="absolute flex items-center justify-center flex-col">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const isFirstTimeClick = handlePreviewPlayAndPause();
                      if (isFirstTimeClick) {
                        if (audio.is_purchased) {
                          handleMusicItemPreviewClick(audio);
                        } else {
                          setProgress(0);
                          setIsPreviewLoading(true);
                          setSelectedAudio(audio);
                          setPreviewInPlayMode(true);
                        }
                      }
                    }}
                    className="h-16 w-16 bg-[#1D1159E5] rounded-full p-3 border-4 border-secondary"
                  >
                    {isPlaying && isPreviewPlaying ? (
                      <img
                        src={PauseIconAudio}
                        alt=""
                        className="w-full h-full"
                      />
                    ) : (
                      <img
                        src={PlayIconAudio}
                        alt=""
                        className="w-full h-full"
                      />
                    )}
                  </button>
                  {audio.is_purchased ? (
                    <span className="text-secondary text-center text-lg text-border-1">
                      Play Media
                    </span>
                  ) : isPlaying ? (
                    <span className="text-secondary text-center text-lg text-border-1">
                      {audioTimer}
                    </span>
                  ) : (
                    <span className="text-secondary text-center text-lg text-border-1">
                      Preview media
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
          {isImageLoading && (
            <div className="skeleton w-full h-full absolute !rounded-[10px_35px_25px_40px] z-[0]"></div>
          )}
          {isPlaying && previewInPlayMode && (
            <div className="w-full h-full absolute">
              {isPreviewLoading && (
                <div className="w-full h-full absolute z-50">
                  <p className="video-loader left-[45%] -translate-x-1/2 absolute top-[105px]"></p>
                </div>
              )}
              {isPreviewPlaying && (
                <div className="absolute bottom-3 right-6 z-10">
                  <img
                    src={MusicPlayingGif}
                    className="w-9 h-7"
                    alt="music-playing-gif"
                  />
                </div>
              )}
              <audio
                ref={audioPreviewRef}
                src={selectedAudio?.trailer}
                controls
                className="absolute hidden"
                onEnded={() => {
                  setProgress(0);
                  setAudioTimer("00:00");
                  setPreviewInPlayMode(false);
                  audioPreviewRef.current = null;
                  setIsPreviewPlaying(false);
                  setIsPreviewLoading(false);
                  setSelectedAudio(undefined);
                }}
                onPlay={() => {
                  setIsPreviewPlaying(true);
                }}
                onPause={() => {
                  setIsPreviewPlaying(false);
                }}
                onError={() => {
                  setIsPreviewLoading(false);
                }}
                onWaiting={() => {
                  setIsPreviewLoading(true);
                }}
                onCanPlay={() => {
                  setIsPreviewLoading(false);
                }}
                onLoadedMetadata={() => {
                  if (audioPreviewRef.current) audioPreviewRef.current.play();
                  setIsPreviewPlaying(true);
                  setIsPreviewLoading(false);
                }}
              />
            </div>
          )}
          <div className="absolute bottom-3 right-6">
            <img
              src={MusicPlayingGif}
              className="w-9 h-7"
              alt="music-playing-gif"
            />
          </div>
          <img
            className="w-full object-cover blur-sm bg-gradient-to-t from-[#184AF4] to-[#182D73] h-full absolute top-0 left-0"
            src={getValueOrDefault(
              getImageUrlWithExpiry(audio?.thumbnail),
              musicIcon
            )}
            alt="music-preview-image"
          />
          <img
            className="w-full object-contain h-full relative"
            src={getValueOrDefault(
              getImageUrlWithExpiry(audio?.thumbnail),
              musicIcon
            )}
            onLoad={() => {
              setIsImageLoading(false);
            }}
            loading="lazy"
            onError={() => {
              setTimeout(() => {
                setIsImageLoading(false);
              }, 3000);
            }}
            alt="music-preview-image"
          />
        </div>
        <div
          className={`relative z-10 -top-6 -left-5 min-h-[19rem] max-sm:h-fit max-sm:min-h-64 max-md:left-0 bg-[#1d1159] rounded-[10px_35px_25px_40px] ${
            audio.is_purchased || !user ? "cursor-pointer" : "cursor-auto"
          }`}
          onClick={() => {
            if (audio.is_purchased) {
              handleMusicItemPreviewClick(audio);
            } else {
              if (!user) {
                setLogin({ type: "updateLogin", payload: true });
                if (isMobile) {
                  searchParams.set("tab", "login");
                  setSearchParams(searchParams, { replace: true });
                }
                return;
              }
            }
          }}
        >
          <div className="p-8 max-sm:pe-4 max-sm:ps-6 max-sm:pt-6 break-words h-full flex flex-col items-start justify-between">
            <div className="w-full">
              <div className="flex max-sm:flex-wrap items-center justify-between gap-3 mb-1 w-full">
                <p className="flex items-center text-lg gap-2 text-primary font-mikado-medium">
                  <i className="ri-music-2-line"></i> Music
                </p>

                <div className="flex items-center gap-2">
                  <p className="text-primary font-mikado-medium">
                    {audio.is_purchased
                      ? ""
                      : "Listen Now " + currencyFormatter(Number(audio.price))}
                  </p>
                  {index !== undefined && addToWishlist && (
                    <div
                      role="button"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (
                          addToWishlist &&
                          index !== undefined &&
                          !audio.is_purchased
                        )
                          await addToWishlist(audio.id, index);
                      }}
                      className={`flex justify-center w-[36px] ${
                        audio.is_purchased
                          ? "opacity-50 cursor-auto"
                          : "opacity-100"
                      }`}
                      aria-label="Like"
                      data-tooltip-id="audio-wishlist-tooltip"
                      data-tooltip-content={
                        !audio.is_purchased
                          ? ""
                          : "You cannot add this music to your wishlist because you have already purchased it."
                      }
                    >
                      {audio.is_wishlist ? (
                        <i className="ri-heart-fill text-4xl max-md:text-3xl max-sm:text-2xl text-primary-gradient"></i>
                      ) : (
                        <i className="ri-heart-line text-4xl max-md:text-3xl max-sm:text-2xl text-primary-gradient"></i>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-primary text-2xl max-sm:text-xl font-mikado-bold line-clamp-1 w-full break-all">
                {audio.title}
              </p>
              <p className="text-[#9CA3AF] text-base font-mikado-regular line-clamp-1 w-full break-all mb-3">
                {
                  convertSecondsIntoHoursWithoutZero(audio.total_duration, 0)
                    .timeInWords
                }
              </p>
              <p className="text-primary text-2xl max-sm:text-lg font-mikado-regular line-clamp-1 w-full break-all media-description mb-2">
                <span className="pr-6 text-[#9CA3AF]">Written by</span>{" "}
                {convertToCommaSeparatedValue(audio.writers)}
              </p>
              <p className="text-primary text-2xl max-sm:text-lg font-mikado-regular line-clamp-1 w-full break-all media-description mb-2">
                <span className="pr-6 text-[#9CA3AF]">Performed by</span>{" "}
                {convertToCommaSeparatedValue(audio.perform_by)}
              </p>
              <p className="text-primary text-2xl max-sm:text-lg font-mikado-regular line-clamp-1 w-full break-all media-description mb-2">
                <span className="pr-6 text-[#9CA3AF]">Produced by</span>{" "}
                {convertToCommaSeparatedValue(audio?.producers)}
              </p>
              <p className="text-primary text-2xl max-sm:text-lg font-mikado-regular line-clamp-1 w-full break-all media-description">
                <span className="pr-6 text-[#9CA3AF]">Release date</span>{" "}
                {audio.release_date}
              </p>
            </div>
            {!audio.is_purchased && (
              <button
                className="flex min-w-[159px] justify-center text-[#1D1159] mt-4 bg-[#d854ba] bg-[url('@assets/ui-helpers/button/ButtonDarkBorder.png')] rounded-3xl bg-no-repeat bg-cover bg-center py-4 px-9 duration-150 hover:scale-95 disabled:hover:scale-100 disabled:opacity-80 disabled:cursor-not-allowed disabled:duration-0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!user) {
                    setLogin({ type: "updateLogin", payload: true });
                    if (isMobile) {
                      searchParams.set("tab", "login");
                      setSearchParams(searchParams, { replace: true });
                    }
                    localStorage.setItem("lID", audio.id.toString());
                    localStorage.setItem("letype", "Music");
                    return;
                  }
                  handleMusicAddToCart(audio.id, index || 0);
                }}
                disabled={audio.is_added_purchased || isMusicAddingLoading}
                aria-label="add-cart-button"
              >
                {audio.is_added_purchased ? "Added" : "Add to cart"}
              </button>
            )}
            {audio.is_purchased && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMusicItemPreviewClick(audio);
                }}
                className="flex min-w-[159px] justify-center text-[#1D1159] mt-4 bg-[#d854ba] bg-[url('@assets/ui-helpers/button/ButtonDarkBorder.png')] rounded-3xl bg-no-repeat bg-cover bg-center py-4 px-9 duration-150 hover:scale-95"
                aria-label="listen-button"
              >
                LISTEN
              </button>
            )}
          </div>
          <img
            className="w-full h-full absolute top-0 left-0 -z-[1]"
            src={musicBorder}
            alt="music-border"
          />
        </div>
      </div>
      <Tooltip
        place="bottom"
        className="break-words !bg-primary !text-black !z-[9999] !text-sm !px-2 !py-1 !max-w-36"
        id="audio-wishlist-tooltip"
      />
    </div>
  );
};

export default AudioItem;
